import React, { useState, useEffect, useRef, useContext } from 'react';
import Creatable from 'react-select/creatable';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {
  HrGrey,
  HrRed,
  HrTransitionDuration,
  HrPrimary,
  HrBorderRadiusInputs,
  HrTransitionTimingCunction,
  HrGray,
  HrPastelGreen,
  HrlightestGrey,
  HrlighterGrey,
  HrFont
} from '@components/styles/hr-styles';
import { isString, get } from 'lodash';
import { useTranslation } from 'react-i18next';
import { DisplaySettingsContext } from '../../../postulation_form/store/display-settings';

export const MyCreatable = styled(Creatable)`
  &.custom-hr-select-container {
    font-family: ${HrFont};
    font-size: 14px;
    width: 100%;
    border-radius: ${HrBorderRadiusInputs};
    height: 40px;

    &.error .custom-hr-select__control {
      border-color: ${HrRed};
      & .custom-hr-select__value-container {
        & .custom-hr-select__placeholder {
          color: ${HrRed};
        }
      }
    }

    & .custom-hr-select__control {
      border: 1px solid ${HrlighterGrey};
      border-radius: ${HrBorderRadiusInputs};
      height: 40px;
      background: ${(props) => props.background ||  '' } ;
      color:  white;

      & .custom-hr-select__value-container {
        padding: 2px 14px;
        & .custom-hr-select__placeholder {
          color: ${HrGrey};
          white-space: nowrap;
          font-size: ${(props) => `${14 + (props.fontSizeSetting || 0)}px`};
        }
      }

      & .custom-hr-select__indicators {
        & .custom-hr-select__indicator-separator {
          display: none;
        }
        & .custom-hr-select__indicator {
          color: ${HrPrimary};
          transition: transform ${HrTransitionDuration} ${HrTransitionTimingCunction};
        }
      }

      & .custom-hr-select__input {
        color : ${(props) => props.color || '' };
      }
    }

    & .custom-hr-select__control--is-disabled {
      background-color: ${(props) => props.backgroundDisabled || HrlightestGrey };
      color: ${HrGrey};
      border-color: ${HrlighterGrey};
      & .custom-hr-select__indicators {
        & .custom-hr-select__indicator {
          color: ${HrGrey};
        }
      }
    }

    & .custom-hr-select__control--is-focused {
      border: 1px solid ${HrGray};
      border-radius: ${HrBorderRadiusInputs};
      box-shadow: none;
    }

    & .custom-hr-select__control--menu-is-open {
      & .custom-hr-select__indicators {
        & .custom-hr-select__indicator-separator {
          display: none;
        }
        & .custom-hr-select__indicator {
          transform: rotateZ(180deg);
          transition: transform ${HrTransitionDuration} ${HrTransitionTimingCunction};
        }
      }
    }

    & .custom-hr-select__menu {
      background-color : ${(props) => props.background || '' };
      color : ${(props) => props.color || '' };
      border-radius: ${HrBorderRadiusInputs};
      box-shadow: none;
      border: 1px solid ${HrGray};
      overflow: hidden;
      margin: 0;
      margin-top: 4px;
      z-index: 3;
      & .custom-hr-select__menu-list {
        font-size: 14px;
        & .custom-hr-select__option {
        }
        & .custom-hr-select__option--is-focused {
          background-color: ${HrPastelGreen};
          color : ${(props) => props.colorFocus || '' };
          font-size: 14px;
        }
      }
    }

    & .custom-hr-select__single-value {
      color: ${(props) => props.color ||  '' }
    }
  }
`;

export const SelectCreatable = ({
  data,
  name,
  id,
  inputId,
  hideSelectedOptions,
  closeMenuOnSelect,
  openMenuOnClick,
  openMenuOnFocus,
  isSearchable,
  isClearable,
  isDisabled,
  isLoading,
  isMulti,
  minMenuHeight,
  maxMenuHeight,
  menuPlacement,
  createOptionPosition,
  placeholder,
  loadingMessage,
  noOptionsMessage,
  selected,
  onChange,
  formState
}) => {
  const { errors } = formState;
  const [t] = useTranslation();
  const refCurrent = useRef(null);
  const [updateData, setUpdateData] = useState(data);
  const createOption = (label) => ({
    label,
    value: label.toLowerCase().replace(/\W/g, '')
  });

  useEffect(() => {
    setUpdateData(data);
  }, [data]);

  useEffect(() => {
    if (data && selected !== null && isString(selected)) {
      const isExist = data.find((d) => {
        return d.label === selected;
      });
      if (!isExist) {
        setUpdateData((prev) => [{ label: selected, value: selected }, ...prev]);
      }
    }
  }, [selected, data]);

  const { darkMode,fontSizeSetting } = useContext(DisplaySettingsContext);

  return (
    <MyCreatable
      background={darkMode ? '#1A1D2B' : ''}
      backgroundDisabled={darkMode ? '#3c3c54' : ''}
      color={darkMode ? 'white' : ''}
      colorFocus={darkMode ? 'black' : ''}
      fontSizeSetting={fontSizeSetting}
      className={`custom-hr-select-container ${get(formState?.errors, inputId) ? 'error' : ''}`}
      classNamePrefix='custom-hr-select'
      value={updateData.find((c) => c.value == selected) || null}
      name={name}
      id={id}
      inputId={inputId}
      options={updateData}
      hideSelectedOptions={hideSelectedOptions}
      closeMenuOnSelect={closeMenuOnSelect}
      openMenuOnClick={openMenuOnClick}
      openMenuOnFocus={openMenuOnFocus}
      isSearchable={isSearchable}
      isClearable={isClearable}
      isDisabled={isDisabled}
      isLoading={isLoading}
      isMulti={isMulti}
      minMenuHeight={minMenuHeight}
      maxMenuHeight={maxMenuHeight}
      menuPlacement={menuPlacement}
      createOptionPosition={createOptionPosition}
      placeholder={placeholder}
      loadingMessage={loadingMessage}
      noOptionsMessage={noOptionsMessage}
      formatCreateLabel={(inputValue) => `${t(`postulant.education.create`)} ${inputValue}`}
      onChange={(e) => {
        onChange(e.value);
      }}
      onCreateOption={(inputValue) => {
        const newOption = createOption(inputValue);
        setUpdateData((prev) => [newOption, ...prev]);
        onChange(inputValue);
      }}
    />
  );
};

SelectCreatable.propTypes = {
  data: PropTypes.array,
  name: PropTypes.string,
  id: PropTypes.string,
  inputId: PropTypes.string,
  hideSelectedOptions: PropTypes.bool,
  closeMenuOnSelect: PropTypes.bool,
  openMenuOnClick: PropTypes.bool,
  openMenuOnFocus: PropTypes.bool,
  isSearchable: PropTypes.bool,
  isClearable: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  isMulti: PropTypes.bool,
  minMenuHeight: PropTypes.number,
  maxMenuHeight: PropTypes.number,
  menuPlacement: PropTypes.string,
  createOptionPosition: PropTypes.string,
  placeholder: PropTypes.string,
  loadingMessage: PropTypes.func,
  noOptionsMessage: PropTypes.func
  // onChange: PropTypes.func,
  // onCreateOption: PropTypes.func
};

SelectCreatable.defaultProps = {
  data: [],
  name: '',
  placeholder: 'Seleccione una opción',
  id: '',
  inputId: '',
  hideSelectedOptions: true,
  closeMenuOnSelect: true,
  openMenuOnClick: true,
  openMenuOnFocus: true,
  isSearchable: true,
  isClearable: false,
  isDisabled: false,
  isLoading: false,
  isMulti: false,
  minMenuHeight: 100,
  maxMenuHeight: 200,
  menuPlacement: 'auto',
  createOptionPosition: 'first',
  loadingMessage: () => '',
  noOptionsMessage: () => 'Sin opciones'
  // onChange: [],
  // onCreateOption: []
};
